import { useEffect, useMemo, useState } from "react";
import "./detailResultPage.css";

const RESULT_TYPE_IMAGE_MAP = {
  帽子船長: "/images/resultType/hat.webp",
  長髮船長: "/images/resultType/longHair.webp",
  鬍鬚船長: "/images/resultType/beard.webp",
  小丑船長: "/images/resultType/clown.webp",
  白領船長: "/images/resultType/whiteCollar.webp",
};

function RadarChart({ values = [], className }) {
  const labels = ["領導力", "決斷力", "親近力", "照顧力", "行動力"];
  const cx = 100;
  const cy = 100;
  const radius = 60;
  const labelRadius = 84;
  const levels = [0.25, 0.5, 0.75, 1];

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const toPoint = (index, ratio, r = radius) => {
    const angle = -Math.PI / 2 + (index * 2 * Math.PI) / labels.length;
    return {
      x: cx + Math.cos(angle) * r * ratio,
      y: cy + Math.sin(angle) * r * ratio,
    };
  };

  const normalized = labels.map((_, index) => {
    const raw = Number(values[index] ?? 0);
    const safe = Number.isFinite(raw) ? clamp(raw, 0, 8) : 0;
    return safe / 8;
  });

  const valuePolygon = normalized
    .map((ratio, index) => {
      const { x, y } = toPoint(index, ratio);
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg
      className={className}
      viewBox="0 0 200 200"
      role="img"
      aria-label="五維雷達圖"
    >
      {levels.map((level) => {
        const polygon = labels
          .map((_, index) => {
            const { x, y } = toPoint(index, level);
            return `${x},${y}`;
          })
          .join(" ");
        return (
          <polygon
            key={`grid-${level}`}
            points={polygon}
            fill="none"
            stroke="rgba(0, 0, 0, 0.2)"
            strokeWidth="1"
          />
        );
      })}

      {labels.map((_, index) => {
        const edge = toPoint(index, 1);
        return (
          <line
            key={`spoke-${index}`}
            x1={cx}
            y1={cy}
            x2={edge.x}
            y2={edge.y}
            stroke="rgba(0, 0, 0, 0.2)"
            strokeWidth="1"
          />
        );
      })}

      <polygon
        points={valuePolygon}
        fill="#d43d5440"
        stroke="#d43d54"
        strokeWidth="2"
      />

      {labels.map((label, index) => {
        const point = toPoint(index, 1, labelRadius);
        return (
          <text
            key={`label-${label}`}
            x={point.x}
            y={point.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="14px"
            fill="#000"
          >
            {label}
          </text>
        );
      })}
    </svg>
  );
}

function DetailResultPage() {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingImage, setDownloadingImage] = useState(false);
  const [resultType, setResultType] = useState("");
  const [overallDescriptions, setOverallDescriptions] = useState({
    description1: "",
    description2: "",
    description3: "",
  });

  const goMainPage = () => window.location.assign("/");

  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );
  const queryName = (searchParams.get("name") || "").trim();

  const resultContext = useMemo(() => {
    try {
      const raw = window.sessionStorage.getItem("superDaddyResultContext");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);

  const inputName = queryName || resultContext.name || "";
  const questionSetId = Number(resultContext.questionSetId);
  const savedQuestionSetScore = Number(resultContext.questionSetScore);

  const dimensionScores = useMemo(
    () => resultContext.dimensionScores || {},
    [resultContext],
  );

  const getRequestBase = () => {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "/api";
    return apiBaseUrl.startsWith("http")
      ? apiBaseUrl
      : `${window.location.origin}${apiBaseUrl}`;
  };

  const radarValues = useMemo(() => {
    return [1, 2, 3, 4, 5].map((dimension) => {
      const score = Number(dimensionScores[dimension] || 0);
      return Number.isFinite(score) && score > 0 ? score : 2;
    });
  }, [dimensionScores]);

  const derivedQuestionSetScore = useMemo(
    () => radarValues.reduce((sum, v) => sum + Number(v || 0), 0),
    [radarValues],
  );

  const questionSetScore =
    Number.isFinite(savedQuestionSetScore) && savedQuestionSetScore > 0
      ? savedQuestionSetScore
      : derivedQuestionSetScore;

  useEffect(() => {
    const controller = new AbortController();

    async function loadDimensionResults() {
      if (!Number.isFinite(questionSetId)) {
        return;
      }

      try {
        const requestBase = getRequestBase();

        const requests = [1, 2, 3, 4, 5].map(async (dimension) => {
          const score = radarValues[dimension - 1] ?? 2;

          const url = new URL(`${requestBase}/results/dimension`);
          url.searchParams.set("questionSetId", String(questionSetId));
          url.searchParams.set("dimension", String(dimension));
          url.searchParams.set("score", String(score));

          const response = await fetch(url.toString(), {
            signal: controller.signal,
          });

          const contentType = response.headers.get("content-type") || "";
          const responseBody = contentType.includes("application/json")
            ? await response.json()
            : { success: false, message: await response.text() };

          if (!response.ok || !responseBody.success) {
            throw new Error(
              responseBody.message || "Failed to load dimension result",
            );
          }

          return {
            dimension,
            score,
            result: responseBody.data?.result || null,
          };
        });

        const loaded = await Promise.all(requests);
        loaded.sort((a, b) => a.dimension - b.dimension);
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }

    loadDimensionResults();
    return () => controller.abort();
  }, [questionSetId, radarValues]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadOverallResult() {
      if (!Number.isFinite(questionSetId) || !Number.isFinite(questionSetScore))
        return;

      try {
        const requestBase = getRequestBase();

        const url = new URL(`${requestBase}/results/overall`);
        url.searchParams.set("questionSetId", String(questionSetId));
        url.searchParams.set("score", String(questionSetScore));

        const response = await fetch(url.toString(), {
          signal: controller.signal,
        });

        const contentType = response.headers.get("content-type") || "";
        const responseBody = contentType.includes("application/json")
          ? await response.json()
          : { success: false, message: await response.text() };

        if (!response.ok || !responseBody.success) return;

        const type = responseBody.data?.result?.type;
        if (type) setResultType(type);

        const result = responseBody.data?.result;
        setOverallDescriptions({
          description1: result?.description_1 || "",
          description2: result?.description_2 || "",
          description3: result?.description_3 || "",
        });
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }

    loadOverallResult();
    return () => controller.abort();
  }, [questionSetId, questionSetScore]);

  const shareSheet = async () => {
    const shareData = {
      title: "Super Daddy 測驗結果",
      text: `我是 ${inputName}，我的神隊友爸爸類型是：${resultType}！`,
      url: shareUrl, // or window.location.href if you want the current page
    };

    if (navigator.share) {
      await navigator.share(shareData);
      return;
    }

    // Fallback (desktop / unsupported browsers)
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareData.url);
      alert("已複製連結，可以貼到社群分享");
    } else {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          shareData.url,
        )}`,
        "_blank",
        "noopener,noreferrer,width=600,height=700",
      );
    }
  };

  //   分享連結先放測試圖，等正式圖上傳後再更新連結
  //   帽子船長: `https://www.superdaddypb.com/share2FB/hat.html`,
  //   長髮船長: `https://www.superdaddypb.com/share2FB/longHair.html`,
  //   鬍鬚船長: `https://www.superdaddypb.com/share2FB/beard.html`,
  //   小丑船長: `https://www.superdaddypb.com/share2FB/clown.html`,
  //   白領船長: `https://www.superdaddypb.com/share2FB/whiteCollar.html`,

  const RESULT_SHARE_LINK_MAP = {
    // 帽子船長: `https://shorturl.at/4KSLG`,
    // 長髮船長: `https://shorturl.at/5dhWr`,
    // 鬍鬚船長: `https://shorturl.at/V3oMW`,
    // 小丑船長: `https://shorturl.at/elsuh`,
    // 白領船長: `https://shorturl.at/eFi3O`,
    帽子船長: `https://www.superdaddypb.com/share2FB/hat.html`,
    長髮船長: `https://www.superdaddypb.com/share2FB/longHair.html`,
    鬍鬚船長: `https://www.superdaddypb.com/share2FB/beard.html`,
    小丑船長: `https://www.superdaddypb.com/share2FB/clown.html`,
    白領船長: `https://www.superdaddypb.com/share2FB/whiteCollar.html`,
  };

  const shareUrl = RESULT_SHARE_LINK_MAP[resultType];

  const shareToFacebook = () => {
    const urlToShare = encodeURIComponent(shareUrl);
    const fbShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${urlToShare}`;
    window.open(
      fbShareUrl,
      "_blank",
      "noopener,noreferrer,width=600,height=700",
    );
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const recordId = Number(resultContext.recordId);
      if (!Number.isFinite(recordId)) {
        throw new Error("缺少 recordId，無法產生 PDF。");
      }

      if (
        !Number.isFinite(questionSetId) ||
        !Number.isFinite(questionSetScore)
      ) {
        throw new Error("缺少題庫或分數資訊，無法產生 PDF。");
      }

      const url = new URL(
        `${process.env.REACT_APP_API_BASE_URL}/records/${recordId}/pdf`,
      );
      url.searchParams.set("questionSetId", String(questionSetId));
      url.searchParams.set("name", inputName);
      url.searchParams.set("score", String(questionSetScore));
      url.searchParams.set("attr1", String(radarValues[0]));
      url.searchParams.set("attr2", String(radarValues[1]));
      url.searchParams.set("attr3", String(radarValues[2]));
      url.searchParams.set("attr4", String(radarValues[3]));
      url.searchParams.set("attr5", String(radarValues[4]));

      const res = await fetch(url.toString());
      if (!res.ok) throw new Error("PDF 產生失敗，請稍後再試。");

      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${inputName || "super-daddy"}-result.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error(e);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const resultTypeImageSrc = RESULT_TYPE_IMAGE_MAP[resultType] || "";

  const handleSaveDetailedImage = async () => {
    setDownloadingImage(true);
    const params = new URLSearchParams({
      name: inputName,
      questionSetId: String(questionSetId),
      score: String(questionSetScore),
      attr1: String(radarValues[0]),
      attr2: String(radarValues[1]),
      attr3: String(radarValues[2]),
      attr4: String(radarValues[3]),
      attr5: String(radarValues[4]),
    });

    const recordId = Number(resultContext.recordId);

    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/records/${recordId}/detail-screenshot?${params.toString()}`;
      const response = await fetch(url);

      if (!response.ok) {
        alert("產生圖片失敗");
        return;
      }

      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "super-daddy-result.png";
      link.click();
      URL.revokeObjectURL(link.href);

    } finally {
      setDownloadingImage(false);
    }

  };

  return (
    <main className="dr-page">
      <div className="dr-page__bg" />

      <section className="dr-card">
        <img
          className="dr-card__paper"
          src="/images/P8_Banner_01.webp"
          alt=""
        />

        <div className="dr-card__content">
          <div className="dr-header">
            <div className="dr-title">
              <div className="dr-title__name">{`${inputName}爸爸`}</div>
              <div className="dr-title__subtitle">專屬你的爸爸潛力分析</div>
            </div>

            <div className="dr-visuals">
              <img
                className="dr-visuals__type"
                src={resultTypeImageSrc || null}
                alt={resultType}
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = null;
                }}
              />
              <RadarChart className="dr-visuals__radar" values={radarValues} />
            </div>
            <div className="dr-subtitle">
              {overallDescriptions.description1}
            </div>
          </div>

          <div className="dr-body">
            <div className="dr-body__description dr-scroll-box">
              <p className="dr-desc">
                詳細評語：
                <br />
                {overallDescriptions.description2}
              </p>
              <p className="dr-desc dr-desc--pre">
                {overallDescriptions.description3
                  .split("\\n")
                  .map((point, index) => (
                    <span key={index}>
                      • {point}
                      <br />
                      <br />
                    </span>
                  ))}
              </p>
            </div>

            <div className="dr-actions">
              <div className="dr-actions__group">
                <button
                  type="button"
                  onClick={shareSheet}
                  className="dr-btn dr-btn--primary"
                >
                  分享給朋友
                </button>

                <button
                  type="button"
                  onClick={shareToFacebook}
                  className="dr-btn dr-btn--primary"
                >
                  分享到 Facebook (Android適用)
                </button>

                <button
                  type="button"
                  onClick={downloadingImage ? undefined : handleSaveDetailedImage}
                  className="dr-btn dr-btn--primary dr-btn--icon"
                  disabled={downloadingImage}
                >
                  {downloadingImage ? (
                    <>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 50 50"
                        style={{ verticalAlign: "middle", marginRight: 6 }}
                      >
                        <circle
                          cx="25"
                          cy="25"
                          r="20"
                          fill="none"
                          stroke="#888"
                          strokeWidth="5"
                          strokeDasharray="31.4 31.4"
                          strokeLinecap="round"
                        >
                          <animateTransform
                            attributeName="transform"
                            type="rotate"
                            from="0 25 25"
                            to="360 25 25"
                            dur="1s"
                            repeatCount="indefinite"
                          />
                        </circle>
                      </svg>
                      圖片準備中...
                    </>
                  ) : (
                    <>
                      <svg width="15px" height="15px" viewBox="0 0 24 24">
                        <path
                          fill="none"
                          stroke="#000"
                          strokeWidth="3"
                          d="M3,2 L3,21 L21,21 L21,3 L12,3 L12,14"
                        />
                        <path d="M12 16 L7 10 L17 10 Z" fill="#000000" />
                      </svg>
                      儲存結果圖
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={downloadingPdf ? undefined : downloadPdf}
                  className="dr-btn dr-btn--primary dr-btn--icon"
                  disabled={downloadingPdf}
                >
                  {downloadingPdf ? (
                    <>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 50 50"
                        style={{ verticalAlign: "middle", marginRight: 6 }}
                      >
                        <circle
                          cx="25"
                          cy="25"
                          r="20"
                          fill="none"
                          stroke="#888"
                          strokeWidth="5"
                          strokeDasharray="31.4 31.4"
                          strokeLinecap="round"
                        >
                          <animateTransform
                            attributeName="transform"
                            type="rotate"
                            from="0 25 25"
                            to="360 25 25"
                            dur="1s"
                            repeatCount="indefinite"
                          />
                        </circle>
                      </svg>
                      PDF 準備中...
                    </>
                  ) : (
                    <>
                      <svg width="15px" height="15px" viewBox="0 0 24 24">
                        <path
                          fill="none"
                          stroke="#000"
                          strokeWidth="3"
                          d="M3,2 L3,21 L21,21 L21,3 L12,3 L12,14"
                        />
                        <path d="M12 16 L7 10 L17 10 Z" fill="#000000" />
                      </svg>
                      儲存結果PDF
                    </>
                  )}
                </button>
              </div>

              <button type="button" onClick={goMainPage} className="dr-home">
                <img
                  className="dr-home-btn-image"
                  src="/images/P2_Home_Button_01.webp"
                  alt="home"
                />
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default DetailResultPage;
